import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { refreshUser } from 'src/api/auth';
import { UserGroup } from 'src/types/users';
import { COOKIE_KEYS } from 'src/utils/cookies';
import { parseJwt } from 'src/utils/parse-jwt';
import Cookies from 'universal-cookie';
import { RootState } from '.';

export const getSessionValues = () => {
  try {
    const cookies = new Cookies();
    const session = decodeURIComponent(cookies.get(COOKIE_KEYS.AUTH.SESSION) || '');
    const decodedSession = atob(session).split('~');

    return {
      clientId: decodedSession[0],
      refreshToken: decodedSession[1],
    };
  } catch {
    return {
      clientId: '',
      refreshToken: '',
    };
  }
};

export const fetchAuthSession = createAsyncThunk('auth/fetchSession', async () => {
  const { clientId, refreshToken } = getSessionValues();

  if (!clientId || !refreshToken) {
    throw new Error('No session found');
  }

  const response = await refreshUser(refreshToken, clientId);
  const parsedUser = parseJwt(response.id_token);

  return {
    email: parsedUser.email,
    givenName: parsedUser.given_name,
    familyName: parsedUser.family_name,
    groups: parsedUser['cognito:groups'],
    userId: parsedUser.sub,
    isSSO: !!parsedUser.identities,

    expires: Number(parsedUser.exp + '000'),
    accessToken: response.access_token,
    idToken: response.id_token,

    participantId: parsedUser['custom:participants'] || '',
    researcherId: parsedUser['custom:researchers'] || '',
    organizationIds: parsedUser['custom:organizations']?.split('|') || [],
    organizationAdminIds: parsedUser['custom:organizations-admin']?.split('|') || [],
  };
});

export interface UserAccount {
  email: string;
  phoneNumber?: string;
  userId: string;
  givenName: string;
  familyName: string;
  groups: UserGroup[];
  isSSO: boolean;

  accessToken: string;
  idToken: string;
  expires: number;

  participantId: string;
  researcherId: string;
  organizationIds: string[];
  organizationAdminIds: string[];
}

export interface AuthState {
  checkingAuthentication: boolean;
  isAuthenticated: boolean;
  featureFlagIdentitySet: boolean;

  account: UserAccount;
}

const initialState: AuthState = {
  checkingAuthentication: true,
  isAuthenticated: false,
  featureFlagIdentitySet: false,
  account: {
    userId: '',
    givenName: '',
    familyName: '',
    email: '',
    phoneNumber: '',
    isSSO: false,

    accessToken: '',
    idToken: '',
    expires: 0,

    participantId: '',
    researcherId: '',
    organizationIds: [],
    organizationAdminIds: [],
    groups: [],
  },
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    signIn: (state, action: PayloadAction<UserAccount>) => {
      const account = action.payload;
      state.isAuthenticated = true;
      state.account = account;
    },
    signOut: (state) => {
      state.isAuthenticated = false;
      state.account = initialState.account;
    },
    authChecked: (state) => {
      state.checkingAuthentication = false;
    },
    featureFlagIdentitySet: (state) => {
      state.featureFlagIdentitySet = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuthSession.pending, (state) => {
        state.checkingAuthentication = true;
      })
      .addCase(fetchAuthSession.fulfilled, (state, action) => {
        state.checkingAuthentication = false;
        state.isAuthenticated = true;
        state.account = action.payload;
      })
      .addCase(fetchAuthSession.rejected, (state) => {
        state.isAuthenticated = false;
        state.checkingAuthentication = false;
      });
  },
});

export const { signIn, signOut, authChecked, featureFlagIdentitySet } = authSlice.actions;
export const authReducer = authSlice.reducer;

// Selectors
// NOTE: adding new selectors here due to be conform with Redux Toolkit v2
// it will be easier to migrate later
export const getInitials = (state: RootState) => {
  const { givenName, familyName } = state.auth.account;

  return `${givenName.charAt(0).toUpperCase()}${familyName.charAt(0).toUpperCase()}`;
};

export const getOrganizationIds = (state: RootState): string[] => {
  return state.auth.account.organizationIds;
};

export const selectIsUserAdmin = (state: RootState): boolean => {
  return state.auth.account.groups.includes(UserGroup.ADMIN) && state.auth.account.organizationAdminIds.length > 0;
};

export enum AuthActions {
  SIGN_IN = 'auth/signIn',
  SIGN_OUT = 'auth/signOut',
  AUTH_CHECKED = 'auth/authChecked',
  FETCH_AUTH_SESSION_FULFILLED = 'auth/fetchSession/fulfilled',
}
